import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActiveTimer, useCreateTimeEntry, useStopTimer, useUpdateTimeEntry } from "@/hooks/useTimeEntries";
import { useToast } from "@/hooks/use-toast";
import { differenceInSeconds } from "date-fns";
import { useAuth } from "./useAuth";

// LocalStorage keys
const PAUSE_STATE_KEY = "timer_pause_state";

interface PauseState {
  [timerId: string]: {
    pausedAt: number;
  };
}

export function useTimer() {
  const { data: activeTimer, isLoading } = useActiveTimer();
  const createTimeEntry = useCreateTimeEntry();
  const stopTimerMutation = useStopTimer();
  const updateTimeEntry = useUpdateTimeEntry();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [seconds, setSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [localPausedAt, setLocalPausedAt] = useState<number | null>(null);

  // --- Helper: Local Storage Sync ---
  const getStoredPauseState = useCallback((): PauseState => {
    try {
      const item = localStorage.getItem(PAUSE_STATE_KEY);
      return item ? JSON.parse(item) : {};
    } catch {
      return {};
    }
  }, []);

  const updateStoredPauseState = useCallback((timerId: string, pausedAt: number | null) => {
    const state = getStoredPauseState();
    if (pausedAt) {
      state[timerId] = { pausedAt };
    } else {
      delete state[timerId];
    }
    localStorage.setItem(PAUSE_STATE_KEY, JSON.stringify(state));
    window.dispatchEvent(new Event("storage")); // Trigger sync
  }, [getStoredPauseState]);

  // --- Effect: Sync State from DB and LocalStorage ---
  useEffect(() => {
    if (!activeTimer) {
      setSeconds(0);
      setIsPaused(false);
      setLocalPausedAt(null);
      return;
    }

    const syncState = () => {
      // Check DB state
      const dbIsPaused = activeTimer.is_paused;
      setIsPaused(dbIsPaused);

      // Check LocalStorage for pause start time
      const state = getStoredPauseState();
      const stored = state[activeTimer.id];
      
      if (dbIsPaused && stored?.pausedAt) {
        setLocalPausedAt(stored.pausedAt);
      } else if (dbIsPaused && !stored?.pausedAt) {
        // Fallback: If DB says paused but we lost local state,
        // we assume paused JUST NOW to avoid jumping time? 
        // OR we just accept we lost the "current pause duration" and it starts counting pause from now.
        // Better: Set it to now to be safe.
        // Actually, preventing this is handled by actions.
        setLocalPausedAt(Date.now());
      } else {
        setLocalPausedAt(null);
      }
    };

    syncState();
    
    // Listen for cross-tab updates
    const handleStorage = () => syncState();
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [activeTimer, getStoredPauseState]);

  // --- Effect: Update Timer Ticks ---
  useEffect(() => {
    if (!activeTimer) return;

    const updateTimer = () => {
      const now = Date.now();
      const start = new Date(activeTimer.start_time).getTime();
      const dbPausedSeconds = activeTimer.paused_duration_seconds || 0;
      
      let currentPauseDuration = 0;
      if (isPaused && localPausedAt) {
        currentPauseDuration = Math.floor((now - localPausedAt) / 1000);
      }

      const totalElapsed = Math.floor((now - start) / 1000);
      const totalPaused = dbPausedSeconds + currentPauseDuration;
      
      setSeconds(Math.max(0, totalElapsed - totalPaused));
    };

    updateTimer(); // Immediate update
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeTimer, isPaused, localPausedAt]);


  // --- Actions ---

  const start = async (entryData: any) => {
    if (!user) return;
    try {
      await createTimeEntry.mutateAsync({
        user_id: user.id,
        category: 'dev', // default fallback
        start_time: new Date().toISOString(),
        ...entryData
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao iniciar", variant: "destructive" });
    }
  };

  const stop = async (updates?: any) => {
    if (!activeTimer) return;

    // Calculate final numbers
    const now = Date.now();
    const start = new Date(activeTimer.start_time).getTime();
    
    // Calculate total pause
    const dbPausedSeconds = activeTimer.paused_duration_seconds || 0;
    let currentPauseDuration = 0;
    
    // If we are currently paused, we add the current session
    if (activeTimer.is_paused) {
      const state = getStoredPauseState();
      const stored = state[activeTimer.id];
      if (stored?.pausedAt) {
         currentPauseDuration = Math.floor((now - stored.pausedAt) / 1000);
      } else {
         // Fallback logic if we lost local state
      }
    }

    const totalPaused = dbPausedSeconds + currentPauseDuration;
    const finalDurationMs = (now - start) - (totalPaused * 1000);
    const durationMinutes = Math.max(0, Math.round(finalDurationMs / 60000));

    try {
      await stopTimerMutation.mutateAsync({
        id: activeTimer.id,
        end_time: new Date().toISOString(),
        duration_minutes: durationMinutes,
        paused_duration_seconds: totalPaused > 0 ? totalPaused : null,
        is_paused: false,
        ...updates
      } as any); // using any to bypass strict type check for now if needed

      // Clear local state
      updateStoredPauseState(activeTimer.id, null);
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao parar", variant: "destructive" });
    }
  };

  const pause = async () => {
    if (!activeTimer) return;
    try {
      // 1. Set local state IMMEDIATELY
      updateStoredPauseState(activeTimer.id, Date.now());
      setIsPaused(true);
      setLocalPausedAt(Date.now());

      // 2. Update DB
      await updateTimeEntry.mutateAsync({
        id: activeTimer.id,
        is_paused: true
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao pausar", variant: "destructive" });
    }
  };

  const resume = async () => {
    if (!activeTimer) return;
    try {
      // 1. Calculate accumulated pause
      const state = getStoredPauseState();
      const stored = state[activeTimer.id];
      let addedPause = 0;
      if (stored?.pausedAt) {
        addedPause = Math.floor((Date.now() - stored.pausedAt) / 1000);
      }

      const newTotalPaused = (activeTimer.paused_duration_seconds || 0) + addedPause;

      // 2. Update DB
      await updateTimeEntry.mutateAsync({
        id: activeTimer.id,
        is_paused: false,
        paused_duration_seconds: newTotalPaused
      });

      // 3. Clear local state
      updateStoredPauseState(activeTimer.id, null);
      setIsPaused(false);
      setLocalPausedAt(null);

    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao retomar", variant: "destructive" });
    }
  };

  return {
    activeTimer,
    seconds,
    isPaused,
    start,
    stop,
    pause,
    resume
  };
}
